import React from 'react';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2/Table';
import { SortingState } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon, ArrowDownArrowUpIcon } from '@revfluence/fresh-icons/regular/esm';
import { BoltIcon, TagsIcon, SackDollarIcon, GiftIcon } from '@revfluence/fresh-icons/solid/esm';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { Button } from '@/shadcn/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { GetBoostsPerformanceStatsForOffer_boostPerformances } from '../../queries/types/GetBoostsPerformanceStatsForOffer';
import { BOOST_TYPE, BoostEligibilitySelectionType } from '../../types/globalTypes';
import { GetAllBoostData_boostDataForAllAffiliatesInOffer } from '../../queries/types/GetAllBoostData';

const SortButton = ({ column, children }: { column: { getIsSorted: () => false | 'asc' | 'desc'; toggleSorting: (desc?: boolean) => void; clearSorting: () => void }; children: React.ReactNode }) => {
    const isSorted = column.getIsSorted();
    const [isHovering, setIsHovering] = React.useState(false);

    const handleSort = () => {
        if (isSorted === 'asc') {
            column.toggleSorting(true);
        } else if (isSorted === 'desc') {
            column.clearSorting();
        } else {
            column.toggleSorting(false);
        }
    };

    return (
      <Button
        variant="ghost"
        className="px-2 hover:bg-transparent w-full"
        onClick={handleSort}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="flex w-full justify-between items-center">
          <span>{children}</span>
          <span className="h-3 w-3 flex items-center justify-center">
            {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
            {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
            {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
          </span>
        </div>
      </Button>
    );
};

const getBoostIcon = (boostType: string) => {
    switch (boostType) {
        case BOOST_TYPE.DISCOUNT_BOOST:
            return (
              <div className="flex items-center justify-center rounded-full bg-teal-4 w-6 h-6">
                <TagsIcon className="text-grey-0 w-3 h-3" />
              </div>
            );
        case BOOST_TYPE.CASHBACK_BOOST:
            return (
              <div className="flex items-center justify-center rounded-full bg-yellow-4 w-6 h-6">
                <SackDollarIcon className="h-3 w-3 text-grey-0" />
              </div>
            );
        case BOOST_TYPE.PFA_BOOST:
            return (
              <div className="flex items-center justify-center rounded-full bg-magenta-4 w-6 h-6">
                <GiftIcon className="text-grey-0 w-3 h-3" />
              </div>
            );
        default:
            return <BoltIcon className="h-4 w-4 text-gray-500" />;
    }
};

const getBoostDisplayName = (boostType: string, boostId: number) => {
    switch (boostType) {
        case 'DISCOUNT_BOOST':
            return `Discount Boost #${boostId}`;
        case 'CASHBACK_BOOST':
            return `Cashback Boost #${boostId}`;
        case 'PFA_BOOST':
            return `Free Product Boost #${boostId}`;
        default:
            return `Boost #${boostId}`;
    }
};

const calculateEligibleMembers = (
    boost: GetAllBoostData_boostDataForAllAffiliatesInOffer | undefined,
    totalOfferMembers: number
): string | number => {
    if (!boost?.boost.boostEligibility) {
        return 0;
    }

    const eligibility = boost.boost.boostEligibility;

    // If advanced selection metadata is present, return "NA"
    if (eligibility.advancedSelectionMetadata) {
        return 'NA';
    }

    switch (eligibility.eligibilityType) {
        case BoostEligibilitySelectionType.ALL:
            return totalOfferMembers;

        case BoostEligibilitySelectionType.INCLUDE:
            return eligibility.memberIds?.length || 0;

        case BoostEligibilitySelectionType.EXCLUDE:
            return totalOfferMembers - (eligibility.memberIds?.length || 0);

        case BoostEligibilitySelectionType.ADVANCED:
            return 'NA';

        default:
            return 0;
    }
};

const columns = [
    {
        accessorKey: 'name',
        header: ({ column }) => <SortButton column={column}>Name</SortButton>,
        cell: ({ row }) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0">{row.original.icon}</div>
                  <span className="truncate">{row.original.name}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]"
              >
                {row.original.name}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
        size: 280,
        minSize: 280,
        maxSize: 280,
    },
    {
        accessorKey: 'eligibleMembers',
        header: ({ column }) => <SortButton column={column}>Eligible Members</SortButton>,
        cell: ({ row }) => {
            const value = row.original.eligibleMembers;
            if (value === 'NA') {
                return (
                  <div className="text-right">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">NA</span>
                      </TooltipTrigger>
                      <TooltipContent
                        className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[300px]"
                      >
                        <p>This boost has advanced selection criteria. Eligible member count is calculated at runtime.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                );
            }
            return <div className="text-right">{typeof value === 'number' ? value.toLocaleString() : value}</div>;
        },
    },
    {
        accessorKey: 'waitingForApproval',
        header: ({ column }) => <SortButton column={column}>Waiting for approval</SortButton>,
        cell: ({ row }) => <div className="text-right">{row.original.waitingForApproval}</div>,
    },
    {
        accessorKey: 'awardedTo',
        header: ({ column }) => <SortButton column={column}>Awarded</SortButton>,
        cell: ({ row }) => <div className="text-right">{row.original.awardedTo}</div>,
    },
    {
        accessorKey: 'redeemed',
        header: ({ column }) => <SortButton column={column}>Redeemed</SortButton>,
        cell: ({ row }) => <div className="text-right">{row.original.redeemed}</div>,
    },
    {
        accessorKey: 'cost',
        header: ({ column }) => <SortButton column={column}>Cost</SortButton>,
        cell: ({ row }) => <div className="text-right">{row.original.cost}</div>,
    },
    {
        accessorKey: 'value',
        header: ({ column }) => <SortButton column={column}>Value</SortButton>,
        cell: ({ row }) => <div className="text-right">{row.original.value}</div>,
    },
];

interface Props {
    data?: { data: GetBoostsPerformanceStatsForOffer_boostPerformances[], boosts: GetAllBoostData_boostDataForAllAffiliatesInOffer[] }
}

export function BoostsPerformanceTable({ data }: Props) {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [showAll, setShowAll] = React.useState(false);
    const { offerMembers } = useOfferDetailsContext();

    const tableData = React.useMemo(() => {
        if (!data || !data.data || data.data.length === 0) return [];

        return data.data.map((boostPerformance) => {
            // Find the corresponding boost to get the label
            const boost = data.boosts?.find((b) => b.boost.id === boostPerformance.boostId);
            const boostName = boost?.boost.label || getBoostDisplayName(boostPerformance.boostType, boostPerformance.boostId);

            return {
                boostId: boostPerformance.boostId,
                name: boostName,
                icon: getBoostIcon(boostPerformance.boostType),
                eligibleMembers: calculateEligibleMembers(boost, offerMembers?.length || 0),
                waitingForApproval: boostPerformance.pendingRewards ?? 'N/A',
                awardedTo: boostPerformance.affiliatesRewarded ?? 'N/A',
                redeemed: boostPerformance.redeemedRewards ?? 'N/A',
                cost: boostPerformance.cost ? `$${boostPerformance.cost.toFixed(2)}` : 'N/A',
                value: boostPerformance.value ? `$${boostPerformance.value.toFixed(2)}` : 'N/A',
            };
        });
    }, [data, offerMembers]);

    const sortedData = React.useMemo(() => {
        if (!sorting.length) return tableData;
        const [{ id, desc }] = sorting;
        return [...tableData].sort((a, b) => {
            const aValue = a[id as keyof typeof a];
            const bValue = b[id as keyof typeof b];
            if (aValue === bValue) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return desc ? bValue - aValue : aValue - bValue;
            }
            return desc
                ? String(bValue).localeCompare(String(aValue))
                : String(aValue).localeCompare(String(bValue));
        });
    }, [tableData, sorting]);

    const visibleData = showAll ? sortedData : sortedData.slice(0, 3);
    const totalCount = sortedData.length;
    return (
      <TooltipProvider>
        <div className="border-t border-l border-r rounded-lg">
          <DataTable
            columns={columns}
            data={visibleData}
            enableColumnResizing={false}
            getRowId={(row) => `${row.boostId}`}
            className="min-w-[900px]"
            onSortingChange={setSorting}
          />
          {totalCount > 3 && (
            <div className="flex justify-center mt-4">
              <button
                className="relative text-sm font-medium text-primary underline underline-offset-4 flex items-center"
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll ? 'Show Less' : 'View All Rewards'}
              </button>
            </div>
                )}
        </div>
      </TooltipProvider>
    );
}

export default BoostsPerformanceTable;
